'use client';

import { useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useGlobalContext } from '@/store/contexts/global-context';
import type { Experiment } from '@/types/aggregation/home';
import { AMPLITUDE_EXPERIMENTS, isExperimentEnabled } from '@/utils/experiments';

type MobileWebGameLaunchGateContext = {
    isAndroidWrapper: boolean;
    isIosWrapper: boolean;
    isDesktop: boolean;
    isXsellWrapper: boolean;
    experiments: Experiment;
};

/**
 * Mobile web (no native wrapper, not desktop) may only launch games when
 * `enable-game-launch-mobile-web` is on — blocked by default. Xsell runs its own
 * launch bridge and is exempt.
 */
export const isMobileWebGameLaunchBlocked = ({
    isAndroidWrapper,
    isIosWrapper,
    isDesktop,
    isXsellWrapper,
    experiments
}: MobileWebGameLaunchGateContext): boolean => {
    const isMobileBrowser = !isAndroidWrapper && !isIosWrapper && !isDesktop;
    const isGameLaunchAllowed = isExperimentEnabled(experiments, AMPLITUDE_EXPERIMENTS.ENABLE_GAME_LAUNCH_MOBILE_WEB);

    return !isXsellWrapper && isMobileBrowser && !isGameLaunchAllowed;
};

/** Prismic-backed static pages; `isAndroid` comes from the server-parsed user agent. */
export const getDownloadAppPath = (isAndroid: boolean): string => `/download-app-${isAndroid ? 'android' : 'ios'}`;

/**
 * Shared mobile-web launch gate for every game launch entry point — lobby tiles
 * (`game-url.tsx`) and promo / short links (`usePromotionLauncher`).
 * */
export const useMobileWebGameLaunchGate = () => {
    const router = useRouter();
    const { isAndroid, isAndroidWrapper, isIosWrapper, isDesktop, isXsellWrapper, experiments } = useGlobalContext();

    const isGameLaunchBlockedOnMobileWeb = isMobileWebGameLaunchBlocked({
        isAndroidWrapper,
        isIosWrapper,
        isDesktop,
        isXsellWrapper,
        experiments
    });

    const redirectToDownloadApp = useCallback(() => {
        router.replace(getDownloadAppPath(isAndroid));
    }, [router, isAndroid]);

    return { isGameLaunchBlockedOnMobileWeb, redirectToDownloadApp };
};
